import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, X, RotateCcw } from "lucide-react";
import { StatusBadge } from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export const StepRail = ({ steps = [] }) => (
  <ol className="flex gap-1.5 mt-4" aria-label="Task steps">
    {steps.map((s) => (
      <li key={s.key} title={`${s.label}: ${s.status}`} className="flex-1">
        <div className={`h-[2px] ${s.status === "done" ? "bg-[#C0C0C0]" : s.status === "running" ? "bg-[#D8BFD8] animate-pulse" : s.status === "failed" ? "bg-[#e2a6a6]" : "bg-[#2A2A2A]"}`} />
        <span className="hidden sm:block mt-2 text-[0.6rem] tracking-[0.16em] uppercase text-[#6a6a6a]">{s.label}</span>
      </li>
    ))}
  </ol>
);

export const SourceList = ({ sources = [] }) => (
  <ol className="space-y-2.5" data-testid="task-sources">
    {sources.map((s) => (
      <li key={s.url} className="flex gap-3 text-sm">
        <span className="text-[#D8BFD8] font-display text-base w-6 shrink-0">{s.n}</span>
        <a href={s.url} target="_blank" rel="noreferrer noopener" data-testid={`source-link-${s.n}`} className="text-[#C0C0C0] hover:text-ivory transition-colors duration-200 break-all">
          {s.title} <ExternalLink className="inline h-3 w-3 ml-1 opacity-60" strokeWidth={1.5} />
          <span className="block text-xs text-[#5f5f5f] mt-0.5">{new URL(s.url).hostname}</span>
        </a>
      </li>
    ))}
  </ol>
);

export const TaskCard = ({ task, onChange }) => {
  const active = ["queued", "running"].includes(task.status);
  const act = async (verb) => {
    try { await api.post(`/tasks/${task.id}/${verb}`); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`task-card-${task.id}`}
      className="mt-4 border border-[#2A2A2A] bg-[#0f0f0f] p-5 md:p-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Research task</p>
          <Link to={`/tasks/${task.id}`} className="font-display text-xl text-ivory hover:text-[#D8BFD8] transition-colors" data-testid={`task-card-link-${task.id}`}>{task.title}</Link>
        </div>
        <StatusBadge status={task.status} testId={`task-card-status-${task.id}`} />
      </div>
      <StepRail steps={task.steps} />
      {task.error && <p className="mt-4 text-sm text-[#e2a6a6]" data-testid={`task-card-error-${task.id}`}>{task.error}</p>}
      {task.status === "completed" && task.result?.sources && (
        <div className="mt-5 pt-5 border-t border-[#1c1c1c]"><p className="eyebrow mb-3">Sources</p><SourceList sources={task.result.sources} /></div>
      )}
      <div className="mt-5 flex gap-4">
        {active && <button onClick={() => act("cancel")} data-testid={`task-cancel-${task.id}`} className="text-[0.65rem] tracking-[0.2em] uppercase text-[#8A8A8A] hover:text-[#e2a6a6] inline-flex items-center gap-1.5 transition-colors"><X className="h-3 w-3" />Cancel</button>}
        {["failed", "cancelled"].includes(task.status) && <button onClick={() => act("retry")} data-testid={`task-retry-${task.id}`} className="text-[0.65rem] tracking-[0.2em] uppercase text-[#8A8A8A] hover:text-ivory inline-flex items-center gap-1.5 transition-colors"><RotateCcw className="h-3 w-3" />Retry</button>}
      </div>
    </motion.div>
  );
};

export const ApprovalCard = ({ approval, onChange }) => {
  const decide = async (verb) => {
    try { await api.post(`/approvals/${approval.id}/${verb}`); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pending = approval.status === "pending";
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`approval-card-${approval.id}`}
      className={`mt-4 border p-5 md:p-6 max-w-2xl bg-[#0f0f0f] ${pending ? "border-[#D8BFD8]/40" : "border-[#2A2A2A]"}`}>
      <div className="flex items-center justify-between gap-4">
        <p className="eyebrow !text-[#D8BFD8]">{pending ? "Draft \u00b7 not sent" : "Approval"}</p>
        <StatusBadge status={approval.status} testId={`approval-status-${approval.id}`} />
      </div>
      <p className="mt-4 text-xs text-[#8A8A8A] tracking-wide">WhatsApp to +{approval.payload?.to}</p>
      <p className="mt-2 text-ivory leading-relaxed whitespace-pre-wrap border-l border-[#2A2A2A] pl-4">{approval.payload?.text}</p>
      {approval.result?.error && <p className="mt-4 text-sm text-[#e8d5a8]" data-testid={`approval-error-${approval.id}`}>{approval.result.error}</p>}
      {pending && (
        <div className="mt-6 flex gap-3">
          <button onClick={() => decide("approve")} data-testid={`approval-approve-${approval.id}`} className="px-5 py-2.5 rounded-full bg-[#C0C0C0] text-[#0A0A0A] text-xs tracking-[0.16em] uppercase hover:bg-[#F8F4E6] transition-colors">Approve & send</button>
          <button onClick={() => decide("reject")} data-testid={`approval-reject-${approval.id}`} className="px-5 py-2.5 rounded-full border border-[#2A2A2A] text-[#C0C0C0] text-xs tracking-[0.16em] uppercase hover:border-[#C0C0C0]/50 transition-colors">Discard</button>
        </div>
      )}
    </motion.div>
  );
};
